import { z } from 'zod';
import { HealthSchemas } from '../domain/health';

/**
 * API contracts for health check endpoints
 * Using Base+Variants pattern with proper request/response pairs
 */

/**
 * Request schemas for health endpoints
 * All health endpoints are GET requests with no parameters
 */
const EmptyRequestSchema = z.object({});

/**
 * Health API collection - all /health routes
 * Following the Base+Variants pattern for scalable schema architecture
 */
export const HealthAPI = {
  /**
   * GET /health - Main health check endpoint
   * Returns comprehensive system health information
   */
  health: {
    request: EmptyRequestSchema,
    response: HealthSchemas.full,
    errorResponse: HealthSchemas.error,
  },

  /**
   * GET /health/ready - Readiness check endpoint
   * Returns service readiness status (used for orchestration)
   */
  ready: {
    request: EmptyRequestSchema,
    response: HealthSchemas.readiness,
    errorResponse: HealthSchemas.error,
  },

  /**
   * GET /health/live - Liveness check endpoint
   * Returns minimal app running status (used for load balancers)
   */
  live: {
    request: EmptyRequestSchema,
    response: HealthSchemas.liveness,
    // Note: liveness endpoint doesn't have error responses in current implementation
  },
};

// Export request and response types for each endpoint
export type HealthCheckRequest = z.infer<typeof HealthAPI.health.request>;
export type HealthCheckResponse = z.infer<typeof HealthAPI.health.response>;
export type HealthCheckErrorResponse = z.infer<
  typeof HealthAPI.health.errorResponse
>;

export type ReadinessCheckRequest = z.infer<typeof HealthAPI.ready.request>;
export type ReadinessCheckResponse = z.infer<typeof HealthAPI.ready.response>;
export type ReadinessCheckErrorResponse = z.infer<
  typeof HealthAPI.ready.errorResponse
>;

export type LivenessCheckRequest = z.infer<typeof HealthAPI.live.request>;
export type LivenessCheckResponse = z.infer<typeof HealthAPI.live.response>;
